import { useCheckVersion } from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  Header,
  isIos,
  isNative,
  LogoIcon,
  Modal,
  TextBold,
  TextReg,
  useBanner,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useState } from 'react';
import { Linking, StyleSheet, View } from 'react-native';
import { getVersionInfo } from '../../utils/auth';
import { APP_STORE_APPLE_URL, APP_STORE_GOOGLE_URL } from '../../utils/env';

export function VersionCheckGuard({ children }: PropsWithChildren) {
  const { data: versions } = useCheckVersion(isNative);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const styles = useDynamicStyles(createStyles);
  const { showBanner } = useBanner();

  const handleOpenStore = async () => {
    await Linking.openURL(isIos ? APP_STORE_APPLE_URL : APP_STORE_GOOGLE_URL);
  };

  useEffect(() => {
    if (versions && isNative) {
      const { isSupported, isOnLatest } = getVersionInfo(versions);
      if (!isSupported) {
        setIsModalVisible(true);
      } else {
        setIsModalVisible(false);
        if (!isOnLatest) {
          showBanner({
            isBlocking: true,
            text: 'A new version is available!',
            action: {
              label: `Open ${isIos ? 'App' : 'Play'} Store`,
              onPress: async () => {
                await handleOpenStore();
              },
            },
          });
        }
      }
    }
  }, [versions]);

  return (
    <>
      {children}
      <Modal
        visible={isModalVisible}
        hide={() => {}}
        header={<Header mobileLogo={LogoIcon} />}
      >
        <View style={styles.container}>
          <TextBold style={styles.title}>Time to Update!</TextBold>

          <TextReg style={styles.message}>
            A new version of Communiful is available with improvements and fixes
          </TextReg>

          <Button variant={ButtonVariants.PURPLE} onPress={handleOpenStore}>
            Open {isIos ? 'App' : 'Play'} Store
          </Button>
        </View>
      </Modal>
    </>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 32,
      flex: 1,
      paddingBottom: 75,
    },
    title: {
      fontSize: 26,
      textAlign: 'center',
    },
    message: {
      fontSize: 18,
      textAlign: 'center',
    },
  });
